/**
 * "Custom CSS" — Widget section.
 * The Custom CSS escape hatch in the Widget Design Studio: how the CSS is
 * applied, the STABLE selector + token vocabulary customers may target, the
 * cascade guarantees, what the validator blocks, and worked examples.
 * Reconciled from apps/widget theme (custom-css-injection, public-css-contract)
 * and the shared `validateCustomCss` rules; the selector/token lists below are
 * pinned by `apps/widget/src/theme/public-css-contract.test.ts` — when that
 * contract changes, this page changes in the same PR.
 */
import type { DocPageContent } from '../types';

const SELECTORS = `/* Launcher (the closed floating button — an attribute, not a class) */
[data-syncanix='launcher']
.syncanix-launcher-avatar
.syncanix-launcher-brand

/* Panel chrome */
.syncanix-panel
.syncanix-panel-header

/* Messages */
.syncanix-panel-item-user
.syncanix-panel-item-assistant
.syncanix-msg-body

/* Composer */
.syncanix-panel-composer
.syncanix-panel-composer-form
.syncanix-panel-composer-input
.syncanix-panel-composer-submit
.syncanix-panel-composer-stop
.syncanix-panel-composer-limit

/* Rich primitives — each renders with its type on the root */
.syncanix-card
.syncanix-confirm-card
[data-syncanix-primitive='list' | 'table' | 'card' | 'markdown' | 'button'
  | 'detail' | 'form' | 'status' | 'empty-state' | 'confirm-card' | 'navigate'
  | 'component' | 'iframe' | 'action-plan' | 'action-in-progress'
  | 'action-result' | 'chart' | 'embed' | 'metric' | 'funnel' | 'flow'
  | 'sign-in-card' | 'onboarding-checklist']`;

const TOKENS = `/* Colour */
--syncanix-primary            --syncanix-primary-foreground
--syncanix-background         --syncanix-foreground
--syncanix-muted              --syncanix-muted-foreground
--syncanix-border             --syncanix-ring
--syncanix-destructive        --syncanix-destructive-foreground
--syncanix-link

/* Typography */
--syncanix-font-family        --syncanix-font-family-heading
--syncanix-line-height-base   --syncanix-letter-spacing-base
--syncanix-measure

/* Shape */
--syncanix-radius             --syncanix-radius-sm
--syncanix-radius-md          --syncanix-radius-lg
--syncanix-radius-full        --syncanix-avatar-radius

/* Elevation, spacing, motion, icons */
--syncanix-shadow-lg          --syncanix-shadow-xl
--syncanix-spacing            --syncanix-spacing-sm
--syncanix-motion-duration    --syncanix-motion-easing
--syncanix-icon-stroke-width

/* Per-element studio overrides (set on a descendant to override locally) */
--syncanix-launcher-bg        --syncanix-launcher-fg
--syncanix-header-bg          --syncanix-header-fg
--syncanix-user-bubble-bg     --syncanix-user-bubble-fg
--syncanix-assistant-bubble-bg  --syncanix-assistant-bubble-fg
--syncanix-composer-bg        --syncanix-pane-bg
--syncanix-ai-font-family     --syncanix-user-font-family`;

const HOST_CAVEAT = `/* Does NOT work — tokens are applied inline on the host element,
   which beats any :host rule: */
:host { --syncanix-primary: #0f766e; }

/* Works — re-declare the variable on a descendant instead: */
.syncanix-panel { --syncanix-primary: #0f766e; }`;

const DARK_EXAMPLE = `.syncanix-panel-header { background: #f8fafc; }

:host([data-color-scheme='dark']) .syncanix-panel-header {
  background: #1e293b;
}`;

const EXAMPLE_HEADER = `/* A gradient panel header with a hairline divider */
.syncanix-panel-header {
  background: linear-gradient(135deg, #0f766e, #134e4a);
  border-block-end: 1px solid rgba(255, 255, 255, 0.12);
}`;

const EXAMPLE_PRIMITIVE = `/* Round only the cards, and give tables a tighter radius */
[data-syncanix-primitive='card'] { --syncanix-radius: 1rem; }
[data-syncanix-primitive='table'] { --syncanix-radius: 0.25rem; }`;

const EXAMPLE_MOTION = `/* Slow the widget's motion down, and switch it off for
   reduced-motion visitors */
.syncanix-panel { --syncanix-motion-duration: 300ms; }

@media (prefers-reduced-motion: reduce) {
  .syncanix-panel { --syncanix-motion-duration: 0ms; }
}`;

export const WIDGET_CUSTOM_CSS_PAGE: DocPageContent = {
  slug: 'widget/custom-css',
  titleKey: 'docs.widget.customCss.title',
  descriptionKey: 'docs.widget.customCss.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.customCss.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'how-it-applies',
      textKey: 'docs.widget.customCss.apply.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.apply.body' },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.apply.scope' },

    { kind: 'heading', level: 2, id: 'cascade', textKey: 'docs.widget.customCss.cascade.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.cascade.body' },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.widget.customCss.cascade.caution.title',
      textKey: 'docs.widget.customCss.cascade.caution.body',
    },
    { kind: 'code', language: 'css', code: HOST_CAVEAT },

    {
      kind: 'heading',
      level: 2,
      id: 'selectors',
      textKey: 'docs.widget.customCss.selectors.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.selectors.body' },
    { kind: 'code', language: 'css', code: SELECTORS },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.widget.customCss.selectors.stability.title',
      textKey: 'docs.widget.customCss.selectors.stability.body',
    },

    { kind: 'heading', level: 2, id: 'tokens', textKey: 'docs.widget.customCss.tokens.heading' },
    {
      kind: 'paragraph',
      textKey: 'docs.widget.customCss.tokens.body',
      links: {
        theming: {
          target: { docSlug: 'widget/theming' },
          labelKey: 'docs.widget.customCss.tokens.link',
        },
      },
    },
    { kind: 'code', language: 'css', code: TOKENS },

    { kind: 'heading', level: 2, id: 'dark-mode', textKey: 'docs.widget.customCss.dark.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.dark.body' },
    { kind: 'code', language: 'css', code: DARK_EXAMPLE },

    { kind: 'heading', level: 2, id: 'limits', textKey: 'docs.widget.customCss.limits.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.customCss.limits.lead' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.customCss.limits.item.import',
        'docs.widget.customCss.limits.item.exec',
        'docs.widget.customCss.limits.item.externalUrl',
        'docs.widget.customCss.limits.item.styleClose',
        'docs.widget.customCss.limits.item.disclosure',
        'docs.widget.customCss.limits.item.size',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'examples',
      textKey: 'docs.widget.customCss.examples.heading',
    },
    {
      kind: 'code',
      language: 'css',
      code: EXAMPLE_HEADER,
      captionKey: 'docs.widget.customCss.examples.header',
    },
    {
      kind: 'code',
      language: 'css',
      code: EXAMPLE_PRIMITIVE,
      captionKey: 'docs.widget.customCss.examples.primitive',
    },
    {
      kind: 'code',
      language: 'css',
      code: EXAMPLE_MOTION,
      captionKey: 'docs.widget.customCss.examples.motion',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.customCss.next.theming.title',
          textKey: 'docs.widget.customCss.next.theming.text',
          to: { docSlug: 'widget/theming' },
        },
        {
          titleKey: 'docs.widget.customCss.next.studio.title',
          textKey: 'docs.widget.customCss.next.studio.text',
          to: { docSlug: 'dashboard/design' },
        },
      ],
    },
  ],
};
